import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  UserGroupIcon,
  GlobeAltIcon,
  LockClosedIcon,
  ShareIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CogIcon,
  InformationCircleIcon,
  ArchiveBoxIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const PrivacySettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('visibility');

  useEffect(() => {
    // Simulate fetching privacy settings
    const fetchSettings = () => {
      const mockSettings = {
        visibility: {
          profileVisibility: 'team',
          showOnlineStatus: true,
          showLastSeen: false,
          showWorkingHours: true,
          showContactInfo: true,
          showLocation: true,
          showDepartment: true,
          allowProfileSearch: true,
          showInDirectory: true,
          displayPhotoPublic: false
        },
        
        communication: {
          allowDirectMessages: true,
          allowGroupMessages: true,
          allowExternalMessages: false,
          showTypingIndicator: true,
          showReadReceipts: true,
          allowCallsFromAnyone: false,
          allowCallsFromTeam: true,
          allowCallsFromContacts: true,
          sharePresenceStatus: true,
          allowMentions: true
        },

        dataSharing: {
          shareUsageAnalytics: false,
          shareCrashReports: true,
          allowPersonalization: true,
          shareLocationData: false,
          allowCookies: true,
          allowTargetedAds: false,
          shareWithThirdParties: false,
          allowDataExport: true,
          enableActivityTracking: false,
          allowBehaviorAnalysis: false
        },

        content: {
          shareDocumentsDefault: 'team',
          allowDocumentComments: true,
          shareCalendarDefault: 'private',
          allowCalendarInvites: true,
          shareTasksDefault: 'assigned',
          allowTaskAssignment: true,
          shareNotesDefault: 'private',
          allowContentIndexing: true,
          enableVersionHistory: true,
          autoSaveEnabled: true
        },

        security: {
          requireStrongPasswords: true,
          enableTwoFactor: true,
          allowRememberDevice: true,
          logoutInactiveDevices: 30,
          requireReauth: false,
          allowPasswordSaving: false,
          enableSecurityNotifications: true,
          allowLoginFromNewDevices: true,
          requireApprovalNewDevices: false,
          enableSessionManagement: true
        },

        audit: {
          logProfileViews: true,
          logDocumentAccess: true,
          logLoginActivity: true,
          logDataExports: true,
          logSettingsChanges: true,
          logPermissionChanges: true,
          retentionPeriod: 365,
          allowAuditExport: true,
          notifyOnAuditEvents: false,
          enableRealTimeAlerts: true
        }
      };

      setSettings(mockSettings);
      setLoading(false);
    };

    setTimeout(fetchSettings, 500);
  }, []);

  const handleToggleSetting = (category, setting) => {
    setSaving(true);
    
    setTimeout(() => {
      setSettings(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [setting]: !prev[category][setting]
        }
      }));
      setSaving(false);
    }, 300);
  };

  const handleSelectChange = (category, setting, value) => {
    setSaving(true);
    
    setTimeout(() => {
      setSettings(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [setting]: value
        }
      }));
      setSaving(false);
    }, 300);
  };

  const handleBulkPrivacyUpdate = (level) => {
    setSaving(true);
    
    setTimeout(() => {
      let updates = {};
      
      switch (level) {
        case 'maximum':
          updates = {
            visibility: {
              ...settings.visibility,
              profileVisibility: 'private',
              showOnlineStatus: false,
              showLastSeen: false,
              showContactInfo: false,
              showLocation: false,
              allowProfileSearch: false,
              showInDirectory: false,
              displayPhotoPublic: false
            },
            communication: {
              ...settings.communication,
              allowDirectMessages: false,
              allowExternalMessages: false,
              showTypingIndicator: false,
              showReadReceipts: false,
              allowCallsFromAnyone: false,
              sharePresenceStatus: false
            },
            dataSharing: {
              ...settings.dataSharing,
              shareUsageAnalytics: false,
              allowPersonalization: false,
              shareLocationData: false,
              allowTargetedAds: false,
              shareWithThirdParties: false,
              enableActivityTracking: false,
              allowBehaviorAnalysis: false
            }
          };
          break;
        case 'balanced':
          updates = {
            visibility: {
              ...settings.visibility,
              profileVisibility: 'team',
              showOnlineStatus: true,
              showLastSeen: false,
              showContactInfo: true,
              showLocation: true,
              allowProfileSearch: true,
              showInDirectory: true,
              displayPhotoPublic: false
            },
            communication: {
              ...settings.communication,
              allowDirectMessages: true,
              allowExternalMessages: false,
              showTypingIndicator: true,
              showReadReceipts: true,
              allowCallsFromTeam: true,
              sharePresenceStatus: true
            },
            dataSharing: {
              ...settings.dataSharing,
              shareUsageAnalytics: false,
              allowPersonalization: true,
              shareLocationData: false,
              allowTargetedAds: false,
              shareWithThirdParties: false,
              enableActivityTracking: false
            }
          };
          break;
        case 'open':
          updates = {
            visibility: {
              ...settings.visibility,
              profileVisibility: 'public',
              showOnlineStatus: true,
              showLastSeen: true,
              showContactInfo: true,
              showLocation: true,
              allowProfileSearch: true,
              showInDirectory: true,
              displayPhotoPublic: true
            },
            communication: {
              ...settings.communication,
              allowDirectMessages: true,
              allowExternalMessages: true,
              showTypingIndicator: true,
              showReadReceipts: true,
              allowCallsFromAnyone: true,
              sharePresenceStatus: true
            },
            dataSharing: {
              ...settings.dataSharing,
              shareUsageAnalytics: true,
              allowPersonalization: true,
              shareLocationData: true,
              enableActivityTracking: true
            }
          };
          break;
        default:
          break;
      }
      
      setSettings(prev => ({ ...prev, ...updates }));
      setSaving(false);
    }, 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'visibility', name: 'Profile Visibility', icon: EyeIcon },
    { id: 'communication', name: 'Communication', icon: UserGroupIcon },
    { id: 'dataSharing', name: 'Data & Analytics', icon: ShareIcon },
    { id: 'content', name: 'Content Sharing', icon: DocumentTextIcon },
    { id: 'security', name: 'Security & Access', icon: LockClosedIcon },
    { id: 'audit', name: 'Activity & Audit', icon: ArchiveBoxIcon }
  ];

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      {/* Custom Header for Privacy Settings */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0">
                <ShieldCheckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 sm:mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Privacy Settings</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Control how your information is collected, used, and shared
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
                <Link
                  to="/profile"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <UserCircleIcon className="h-4 w-4 mr-2" />
                  View Profile
                </Link>
                <Link
                  to="/profile/settings"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <CogIcon className="h-4 w-4 mr-2" />
                  Account Settings
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Quick Privacy Level Selection */}
        <div className="bg-white rounded-lg shadow mb-6 sm:mb-8">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Quick Privacy Setup</h3>
            <p className="mt-1 text-sm text-gray-500">
              Choose a privacy level to quickly configure your settings
            </p>
          </div>
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => handleBulkPrivacyUpdate('maximum')}
                disabled={saving}
                className="p-4 border-2 border-red-200 rounded-lg hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                <div className="flex items-center justify-center mb-2">
                  <EyeSlashIcon className="h-8 w-8 text-red-500" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Maximum Privacy</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Minimal sharing, private profile, restricted access
                </p>
              </button>

              <button
                onClick={() => handleBulkPrivacyUpdate('balanced')}
                disabled={saving}
                className="p-4 border-2 border-blue-200 rounded-lg hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <div className="flex items-center justify-center mb-2">
                  <ShieldCheckIcon className="h-8 w-8 text-blue-500" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Balanced</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Team visibility, selective sharing, moderate privacy
                </p>
              </button>

              <button
                onClick={() => handleBulkPrivacyUpdate('open')}
                disabled={saving}
                className="p-4 border-2 border-green-200 rounded-lg hover:border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                <div className="flex items-center justify-center mb-2">
                  <GlobeAltIcon className="h-8 w-8 text-green-500" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Open Sharing</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Public profile, open collaboration, enhanced features
                </p>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Profile Visibility */}
          {activeTab === 'visibility' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Profile Visibility Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Control who can see your profile information and how it appears
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Profile Visibility Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Visibility
                  </label>
                  <select
                    value={settings.visibility.profileVisibility}
                    onChange={(e) => handleSelectChange('visibility', 'profileVisibility', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="private">Private - Only me</option>
                    <option value="team">Team - My team members</option>
                    <option value="department">Department - My department</option>
                    <option value="organization">Organization - All employees</option>
                    <option value="public">Public - Everyone</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    This controls the default visibility of your profile information
                  </p>
                </div>

                {/* Individual Settings */}
                <div className="space-y-4">
                  {Object.entries(settings.visibility)
                    .filter(([key]) => key !== 'profileVisibility')
                    .map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {key === 'showOnlineStatus' && 'Display when you are online or offline'}
                          {key === 'showLastSeen' && 'Show when you were last active'}
                          {key === 'showWorkingHours' && 'Display your working hours and availability'}
                          {key === 'showContactInfo' && 'Make your contact information visible'}
                          {key === 'showLocation' && 'Display your location and office information'}
                          {key === 'showDepartment' && 'Show your department and role information'}
                          {key === 'allowProfileSearch' && 'Allow others to find you in search results'}
                          {key === 'showInDirectory' && 'Include your profile in the team directory'}
                          {key === 'displayPhotoPublic' && 'Make your profile photo visible to everyone'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleToggleSetting('visibility', key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Communication */}
          {activeTab === 'communication' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Communication Preferences</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Control how others can communicate with you and what information is shared
                </p>
              </div>
              <div className="px-6 py-6 space-y-4">
                {Object.entries(settings.communication).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {key === 'allowDirectMessages' && 'Allow others to send you direct messages'}
                        {key === 'allowGroupMessages' && 'Allow addition to group conversations'}
                        {key === 'allowExternalMessages' && 'Receive messages from external users'}
                        {key === 'showTypingIndicator' && 'Show when you are typing a message'}
                        {key === 'showReadReceipts' && 'Show when you have read messages'}
                        {key === 'allowCallsFromAnyone' && 'Accept calls from anyone in the organization'}
                        {key === 'allowCallsFromTeam' && 'Accept calls from team members'}
                        {key === 'allowCallsFromContacts' && 'Accept calls from your contacts only'}
                        {key === 'sharePresenceStatus' && 'Share your current status and availability'}
                        {key === 'allowMentions' && 'Allow others to mention you in conversations'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={() => handleToggleSetting('communication', key)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data Sharing */}
          {activeTab === 'dataSharing' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Data & Analytics</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Control how your data is collected, analyzed, and shared for improving services
                </p>
              </div>
              <div className="px-6 py-6 space-y-4">
                {Object.entries(settings.dataSharing).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {key === 'shareUsageAnalytics' && 'Share usage patterns to improve platform performance'}
                        {key === 'shareCrashReports' && 'Automatically send crash reports to help fix issues'}
                        {key === 'allowPersonalization' && 'Use your data to personalize your experience'}
                        {key === 'shareLocationData' && 'Share location information for relevant features'}
                        {key === 'allowCookies' && 'Allow cookies for enhanced functionality'}
                        {key === 'allowTargetedAds' && 'Show personalized advertisements and content'}
                        {key === 'shareWithThirdParties' && 'Share anonymized data with trusted partners'}
                        {key === 'allowDataExport' && 'Enable data export and backup functionality'}
                        {key === 'enableActivityTracking' && 'Track activity for productivity insights'}
                        {key === 'allowBehaviorAnalysis' && 'Analyze behavior patterns for recommendations'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={() => handleToggleSetting('dataSharing', key)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Sharing */}
          {activeTab === 'content' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Content Sharing Defaults</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Set default sharing levels for documents, calendars, and other content
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Dropdown Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Documents Default Sharing
                    </label>
                    <select
                      value={settings.content.shareDocumentsDefault}
                      onChange={(e) => handleSelectChange('content', 'shareDocumentsDefault', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="private">Private</option>
                      <option value="team">Team</option>
                      <option value="department">Department</option>
                      <option value="organization">Organization</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Calendar Default Sharing
                    </label>
                    <select
                      value={settings.content.shareCalendarDefault}
                      onChange={(e) => handleSelectChange('content', 'shareCalendarDefault', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="private">Private</option>
                      <option value="team">Team</option>
                      <option value="department">Department</option>
                      <option value="organization">Organization</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tasks Default Sharing
                    </label>
                    <select
                      value={settings.content.shareTasksDefault}
                      onChange={(e) => handleSelectChange('content', 'shareTasksDefault', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="private">Private</option>
                      <option value="assigned">Assigned Only</option>
                      <option value="team">Team</option>
                      <option value="department">Department</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes Default Sharing
                    </label>
                    <select
                      value={settings.content.shareNotesDefault}
                      onChange={(e) => handleSelectChange('content', 'shareNotesDefault', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="private">Private</option>
                      <option value="team">Team</option>
                      <option value="department">Department</option>
                      <option value="organization">Organization</option>
                    </select>
                  </div>
                </div>

                {/* Toggle Settings */}
                <div className="space-y-4 pt-4 border-t">
                  {Object.entries(settings.content)
                    .filter(([key]) => !key.includes('Default'))
                    .map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {key === 'allowDocumentComments' && 'Allow others to comment on your shared documents'}
                          {key === 'allowCalendarInvites' && 'Allow others to invite you to calendar events'}
                          {key === 'allowTaskAssignment' && 'Allow others to assign tasks to you'}
                          {key === 'allowContentIndexing' && 'Allow content to be indexed for search'}
                          {key === 'enableVersionHistory' && 'Keep version history of your documents'}
                          {key === 'autoSaveEnabled' && 'Automatically save changes to your content'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleToggleSetting('content', key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Security & Access */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Security & Access Control</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure security settings and access controls for your account
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Numeric Settings */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto-logout Inactive Devices (days)
                  </label>
                  <select
                    value={settings.security.logoutInactiveDevices}
                    onChange={(e) => handleSelectChange('security', 'logoutInactiveDevices', parseInt(e.target.value))}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </select>
                </div>

                {/* Toggle Settings */}
                <div className="space-y-4">
                  {Object.entries(settings.security)
                    .filter(([key]) => key !== 'logoutInactiveDevices')
                    .map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {key === 'requireStrongPasswords' && 'Enforce strong password requirements'}
                          {key === 'enableTwoFactor' && 'Require two-factor authentication for login'}
                          {key === 'allowRememberDevice' && 'Allow trusted devices to be remembered'}
                          {key === 'requireReauth' && 'Require re-authentication for sensitive actions'}
                          {key === 'allowPasswordSaving' && 'Allow browsers to save your password'}
                          {key === 'enableSecurityNotifications' && 'Receive notifications about security events'}
                          {key === 'allowLoginFromNewDevices' && 'Allow login from new devices without approval'}
                          {key === 'requireApprovalNewDevices' && 'Require approval for new device logins'}
                          {key === 'enableSessionManagement' && 'Enable advanced session management features'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleToggleSetting('security', key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Activity & Audit */}
          {activeTab === 'audit' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Activity Logging & Audit</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Control what activities are logged and how long data is retained
                </p>
              </div>
              <div className="px-6 py-6 space-y-6">
                {/* Retention Period */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activity Log Retention Period (days)
                  </label>
                  <select
                    value={settings.audit.retentionPeriod}
                    onChange={(e) => handleSelectChange('audit', 'retentionPeriod', parseInt(e.target.value))}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                    <option value={180}>180 days</option>
                    <option value={365}>1 year</option>
                    <option value={730}>2 years</option>
                  </select>
                </div>

                {/* Logging Settings */}
                <div className="space-y-4">
                  {Object.entries(settings.audit)
                    .filter(([key]) => key !== 'retentionPeriod')
                    .map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {key === 'logProfileViews' && 'Track who views your profile'}
                          {key === 'logDocumentAccess' && 'Track access to your documents'}
                          {key === 'logLoginActivity' && 'Track login and authentication events'}
                          {key === 'logDataExorts' && 'Track when your data is exported'}
                          {key === 'logSettingsChanges' && 'Track changes to your account settings'}
                          {key === 'logPermissionChanges' && 'Track changes to permissions and access'}
                          {key === 'allowAuditExport' && 'Allow export of your activity logs'}
                          {key === 'notifyOnAuditEvents' && 'Receive notifications for audit events'}
                          {key === 'enableRealTimeAlerts' && 'Get real-time alerts for suspicious activity'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleToggleSetting('audit', key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Privacy Impact Notice */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="h-6 w-6 text-blue-400 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Privacy Impact Notice</h3>
              <p className="mt-2 text-sm text-blue-700">
                Your privacy settings affect how you collaborate and what features are available to you. 
                Some features may be limited with higher privacy settings. You can always adjust these 
                settings later to find the right balance for your needs.
              </p>
              <div className="mt-4 flex items-center space-x-4">
                <Link
                  to="/privacy-policy"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/data-usage"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Data Usage Guide
                </Link>
                <button
                  onClick={() => alert('Data download functionality will be implemented')}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                  Download My Data
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Saving Indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Saving...
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default PrivacySettings;
